import { usePage } from "@inertiajs/react";

import { CompanyForm } from "@/components/companies/record-form";
import { DealForm } from "@/components/deals/record-form";
import { PeopleForm } from "@/components/people/record-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { companies_path, deals_path, opportunities_path, people_path } from "@/routes";
import { Company, Deal, Opportunity, Person } from "@/types";

import { OpportunityForm } from "../opportunities/record-form";

export type RecordType = "company" | "deal" | "opportunity" | "person";

/**
 * Title and path, keyed once.
 *
 * There used to be two parallel `switch` statements — one for the form, one
 * for the title — which is two things to keep in step, and the paths were
 * hand-written template strings (`/${account.uid}/deals`) rather than the
 * generated route helpers every other call site uses.
 */
const RECORDS: Record<
  RecordType,
  { title: string; path: (accountUid: string) => string }
> = {
  company: { title: "New company", path: companies_path },
  person: { title: "New person", path: people_path },
  opportunity: { title: "New opportunity", path: opportunities_path },
  deal: { title: "New deal", path: deals_path },
};

interface RecordCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: RecordType;
  // Deal was missing from this union, so every deal call site had to cast.
  onSuccess?: (record: Company | Person | Opportunity | Deal) => void;
  /**
   * What the user had typed into the picker before giving up on finding it
   * (INCO-69). Split on the first space: "Ada Byron" is a first and a last
   * name, "Vitol" is a company name, and either way nobody retypes it.
   */
  prefillName?: string;
}

/** First word into first name, everything after it into last. */
function splitName(value: string) {
  const [first, ...rest] = value.trim().split(/\s+/);
  return { first: first ?? "", last: rest.join(" ") };
}

export function RecordCreateModal({
  open,
  onOpenChange,
  type,
  onSuccess,
  prefillName = "",
}: RecordCreateModalProps) {
  const { props } = usePage();
  const account = props.current_account as { uid: string };
  const companies = (props.companies as Array<Company>) ?? [];
  const opportunities = (props.opportunities as Array<Opportunity>) ?? [];

  const close = () => onOpenChange(false);
  const handleSuccess = (record: Company | Person | Opportunity | Deal) => {
    close();
    onSuccess?.(record);
  };

  const path = RECORDS[type].path(account.uid);

  const form = () => {
    switch (type) {
      case "company":
        return (
          <CompanyForm
            path={path}
            onCancel={close}
            onSuccess={(company) => handleSuccess(company as unknown as Company)}
          />
        );
      case "person":
        return (
          <PeopleForm
            companies={companies}
            path={path}
            firstName={splitName(prefillName).first}
            lastName={splitName(prefillName).last}
            onCancel={close}
            onSuccess={(person) => handleSuccess(person as unknown as Person)}
          />
        );
      case "opportunity":
        return (
          <OpportunityForm
            path={path}
            onCancel={close}
            onSuccess={(opportunity) => handleSuccess(opportunity)}
          />
        );
      case "deal":
        return (
          <DealForm
            opportunities={opportunities}
            path={path}
            onCancel={close}
            onSuccess={(deal) => handleSuccess(deal)}
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{RECORDS[type].title}</DialogTitle>
        </DialogHeader>
        {/*
          Keyed on the prefill so the form starts over when it changes. Radix
          already unmounts closed content, but the initial values are read once
          per mount and a stale name in a "new person" dialog is the kind of
          bug that survives review.
        */}
        <div key={`${type}:${prefillName}`}>{form()}</div>
      </DialogContent>
    </Dialog>
  );
}
